import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import { useDispatch } from "react-redux";
import Sidebar from "../../components/ui/Sidebar";
import Header from "../../components/ui/Header";
import Breadcrumb from "../../components/ui/Breadcrumb";
import QueueOverviewPanel from "./components/QueueOverviewPanel";
import ViewToggle from "./components/ViewToggle";
import TimelineView from "./components/TimelineView";
import TrackerView from "./components/TrackerView";
import EmptyQueueState from "./components/EmptyQueueState";
import BatchCreationModal from "./components/BatchCreationModal";
import Button from "../../components/ui/Button";
import { useCreateBatch } from "./useCreateBatch";
import { useGetBatchStatus } from "pages/upload-invoice/useGetBatchStatus";
import { addNewInvoice } from "./invoiceProgressSlice";
import { useGetInvoiceByName } from "./useGetInvoiceByName";
import { useGetInvoiceStatus } from "./useGetInvoiceStatus";

const InvoiceProcessingQueueContent = () => {
  const { t } = useTranslation("pages");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: batchInfo, refetch } = useGetBatchStatus();
  const { data: invoiceStatuses } = useGetInvoiceStatus();
  const [Iinvoices, setInvoices] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [events, setEvents] = useState([]);
  const { isCollapsed } = useSidebar();
  const [currentView, setCurrentView] = useState("timeline");
  const [isLoading, setIsLoading] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const { mutate: createBatch } = useCreateBatch();

  // Fetch invoice statuses from DB on component mount and dispatch to Redux store
  useEffect(() => {
    if (invoiceStatuses && Array.isArray(invoiceStatuses)) {
      invoiceStatuses.forEach((invoice) => {
        const { invoiceId, invoiceName, batchId, step } = invoice;
        dispatch(
          addNewInvoice({
            invoiceId,
            invoiceName,
            batchId,
            step,
          }),
        );
      });
    }
  }, [invoiceStatuses, dispatch]);

  // Mock invoice data
  const mockInvoices = [
    // {
    //   id: "INV-2025-001",
    //   fileName: "Invoice_ABC_Corp_Jan2025.pdf",
    //   uploadTime: new Date(Date.now() - 1800000), // 30 minutes ago
    //   status: "processing",
    //   currentStep: 3,
    //   totalSteps: 5,
    //   errorMessage: null,
    //   steps: [
    //     { name: "File Conversion", status: "completed", duration: 1200 },
    //     { name: "Schema Validation", status: "completed", duration: 800 },
    //     { name: "VAT/CRN Checks", status: "processing", duration: null },
    //     { name: "AI Anomaly Detection", status: "pending", duration: null },
    //     { name: "ZATCA Clearance", status: "pending", duration: null },
    //   ],
    // },
    // {
    //   id: "INV-2025-002",
    //   fileName: "Invoice_XYZ_Ltd_Jan2025.pdf",
    //   uploadTime: new Date(Date.now() - 3600000), // 1 hour ago
    //   status: "completed",
    //   currentStep: 5,
    //   totalSteps: 5,
    //   errorMessage: null,
    //   steps: [
    //     { name: "File Conversion", status: "completed", duration: 950 },
    //     { name: "Schema Validation", status: "completed", duration: 650 },
    //     { name: "VAT/CRN Checks", status: "completed", duration: 1100 },
    //     { name: "AI Anomaly Detection", status: "completed", duration: 2300 },
    //     { name: "ZATCA Clearance", status: "completed", duration: 1800 },
    //   ],
    // },
    // {
    //   id: "INV-2025-003",
    //   fileName: "Invoice_DEF_Trading_Jan2025.pdf",
    //   uploadTime: new Date(Date.now() - 900000), // 15 minutes ago
    //   status: "error",
    //   currentStep: 2,
    //   totalSteps: 5,
    //   errorMessage:
    //     "VAT number validation failed. Please verify the VAT registration number.",
    //   steps: [
    //     { name: "File Conversion", status: "completed", duration: 1100 },
    //     {
    //       name: "Schema Validation",
    //       status: "error",
    //       duration: null,
    //       error: "Invalid VAT number format",
    //     },
    //     { name: "VAT/CRN Checks", status: "pending", duration: null },
    //     { name: "AI Anomaly Detection", status: "pending", duration: null },
    //     { name: "ZATCA Clearance", status: "pending", duration: null },
    //   ],
    // },
    // {
    //   id: "INV-2025-004",
    //   fileName: "Invoice_GHI_Services_Jan2025.pdf",
    //   uploadTime: new Date(Date.now() - 600000), // 10 minutes ago
    //   status: "processing",
    //   currentStep: 4,
    //   totalSteps: 5,
    //   errorMessage: null,
    //   steps: [
    //     { name: "File Conversion", status: "completed", duration: 890 },
    //     { name: "Schema Validation", status: "completed", duration: 720 },
    //     { name: "VAT/CRN Checks", status: "completed", duration: 980 },
    //     { name: "AI Anomaly Detection", status: "processing", duration: null },
    //     { name: "ZATCA Clearance", status: "pending", duration: null },
    //   ],
    // },
    // {
    //   id: "INV-2025-005",
    //   fileName: "Invoice_JKL_Industries_Jan2025.pdf",
    //   uploadTime: new Date(Date.now() - 300000), // 5 minutes ago
    //   status: "processing",
    //   currentStep: 1,
    //   totalSteps: 5,
    //   errorMessage: null,
    //   steps: [
    //     { name: "File Conversion", status: "processing", duration: null },
    //     { name: "Schema Validation", status: "pending", duration: null },
    //     { name: "VAT/CRN Checks", status: "pending", duration: null },
    //     { name: "AI Anomaly Detection", status: "pending", duration: null },
    //     { name: "ZATCA Clearance", status: "pending", duration: null },
    //   ],
    // },
  ];

  // useEffect(() => {
  //   // Simulate loading
  //   const timer = setTimeout(() => {
  //     setInvoices(mockInvoices);
  //     setIsLoading(false);
  //   }, 1000);

  //   return () => clearTimeout(timer);
  // }, []);

  // Real-time updates simulation
  // useEffect(() => {
  //   if (invoices?.length === 0) return;

  //   const interval = setInterval(() => {
  //     setInvoices((prevInvoices) =>
  //       prevInvoices?.map((invoice) => {
  //         if (invoice?.status === "processing" && Math.random() > 0.7) {
  //           // Randomly advance processing steps
  //           const nextStep = Math.min(
  //             invoice?.currentStep + 1,
  //             invoice?.totalSteps
  //           );
  //           const updatedSteps = invoice?.steps?.map((step, index) => {
  //             if (index < nextStep - 1) {
  //               return {
  //                 ...step,
  //                 status: "completed",
  //                 duration:
  //                   step?.duration || Math.floor(Math.random() * 2000) + 500,
  //               };
  //             } else if (
  //               index === nextStep - 1 &&
  //               nextStep < invoice?.totalSteps
  //             ) {
  //               return { ...step, status: "processing" };
  //             }
  //             return step;
  //           });

  //           return {
  //             ...invoice,
  //             currentStep: nextStep,
  //             status:
  //               nextStep === invoice?.totalSteps ? "completed" : "processing",
  //             steps: updatedSteps,
  //           };
  //         }
  //         return invoice;
  //       })
  //     );
  //   }, 3000);

  //   return () => clearInterval(interval);
  // }, [invoices]);

  const handleRetry = (invoiceId) => {
    setInvoices((prevInvoices) =>
      prevInvoices?.map((invoice) =>
        invoice?.id === invoiceId
          ? {
              ...invoice,
              status: "processing",
              errorMessage: null,
              steps: invoice?.steps?.map((step, index) =>
                index < invoice?.currentStep - 1
                  ? step
                  : {
                      ...step,
                      status:
                        index === invoice?.currentStep - 1
                          ? "processing"
                          : "pending",
                      error: null,
                    },
              ),
            }
          : invoice,
      ),
    );
  };

  const handleViewDetails = (invoiceName, batchId) => {
    console.log("View details for invoice:", invoiceName);
    // Navigate with invoice name in state - will be fetched on the destination page
    navigate(`/invoice-error-correction`, { state: { invoiceName, batchId } });
  };

  // "Correct Error" opens the SAME correction page as "View Details".
  // It must pass invoice context in navigation state, otherwise the
  // destination page has nothing to load.
  const handleCorrectError = (invoiceName, batchId) => {
    navigate("/invoice-error-correction", {
      state: { invoiceName, batchId },
    });
  };

  const handleCreateBatch = (newInvoices, batchInfo) => {
    setInvoices((prev) => [...newInvoices, ...prev]);
    createBatch(batchInfo, {
      onSuccess: async (response) => {
        try {
          const uploads = response.data.uploads;
          var uploadPromises = [];
          // console.log(uploads);

          for (const [index, upload] of uploads.entries()) {
            const formData = new FormData();

            Object.entries(upload.fields).forEach(([key, value]) => {
              formData.append(key, value);
            });

            // The signed policy from the backend includes an
            //   ["eq", "$Content-Type", "application/pdf"]
            // condition, so Content-Type IS part of the policy and MUST be
            // sent with the exact signed value. The file goes LAST.
            formData.append(
              "Content-Type",
              batchInfo?.files[index]?.file?.type || "application/pdf",
            );
            formData.append("file", batchInfo?.files[index]?.file);

            // No custom headers — the browser sets the multipart Content-Type
            // with the correct boundary itself.
            const uploadPromise = fetch(upload.uploadUrl, {
              method: "POST",
              body: formData,
            })
              .then((res) => {
                if (res.ok) {
                  console.log("batchId from backend", response.data.batchId);
                  console.log(`upload successful, ${index + 1}`);
                  const data = {
                    invoiceId: "",
                    batchId: response.data?.batchId || "",
                    invoiceName: batchInfo?.files[index]?.filename,
                    step: "uploaded",
                  };
                  dispatch(addNewInvoice(data));
                  return { success: true, index };
                } else {
                  console.log(`upload unsuccessful ${index + 1}`);
                  return { success: false, index };
                }
              })
              .catch((err) => {
                console.log("upload error for index", index);
                return { success: false, index };
              });

            uploadPromises.push(uploadPromise);
          }
          //wait for all promises to complete
          const results = await Promise.allSettled(uploadPromises);
          //check if all were successfull
          const successfulUploads = results.filter(
            (result) => result.status === "fulfilled" && result.value.success,
          );

          await refetch();

          if (successfulUploads.length === uploads.length) {
            console.log("🎉 All invoices uploaded successfully!");
          } else {
            console.warn(
              `⚠️ ${uploads.length - successfulUploads.length} uploads failed`,
            );
          }
        } catch (err) {
          console.error("Batch upload processing error:", err);
          refetch();
        }
      },
      onError: (error) => {
        console.log(error);
        refetch();
      },
    });

    console.log("Created batch:", newInvoices);
    console.log("Created batch:", batchInfo);
  };

  const handleUploadClick = () => {
    setShowBatchModal(true);
  };

  // Cleared/submitted invoices are marked "completed" and must leave the
  // active queue (they live in the Completion Summary). Failed uploads stay
  // so they can be retried. Empty batches are dropped.
  const activeBatchInfo = (batchInfo || [])
    .map((batch) => ({
      ...batch,
      files: (batch?.files || []).filter((f) => f?.status !== "completed"),
    }))
    .filter((batch) => (batch.files?.length || 0) > 0);

  // Calculate statistics (from the active queue only)
  const allFiles =
    activeBatchInfo?.flatMap((batch) => batch?.files || []) || [];
  const totalInvoices = allFiles?.length;
  const processingCount = allFiles?.filter(
    (inv) => inv?.status === "processing",
  )?.length;
  const completedCount = allFiles?.filter(
    (inv) => inv?.status === "completed",
  )?.length;
  const errorCount = allFiles?.filter(
    (inv) => inv?.status === "error" || inv?.status === "failed",
  )?.length;

  const activeBatches = activeBatchInfo?.length || 0;

  if (isLoading) {
    return (
      <div
        className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ease-in-out ${
          isCollapsed ? "md:ms-16" : "md:ms-64"
        }`}
      >
        <Helmet>
          <title>{`${t("invoiceProcessing.title")} - ZATCA Compliance Management`}</title>
          <meta
            name="description"
            content={t("invoiceProcessing.metaDescription")}
          />
        </Helmet>

        <Header
          title={t("invoiceProcessing.title")}
          description={t("invoiceProcessing.description")}
        />

        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center space-y-6">
            {/* Fixed Loader */}
            <div className="flex justify-center">
              <div className="relative">
                {/* Outer spinning ring */}
                <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                {/* Inner static circle */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <div className="w-3 h-3 bg-primary rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h2 className="text-xl font-semibold text-foreground">
                {t("invoiceProcessing.loadingTitle")}
              </h2>
              <p className="text-muted-foreground max-w-md">
                {t("invoiceProcessing.loadingDescription")}
              </p>

              {/* Progress dots */}
              <div className="flex justify-center gap-2 pt-4">
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                <div
                  className="w-2 h-2 bg-primary rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-primary rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ease-in-out ${
        isCollapsed ? "md:ms-16" : "md:ms-64"
      }`}
    >
      <Helmet>
        <title>{`${t("invoiceProcessing.title")} - ZATCA Compliance Management`}</title>
        <meta
          name="description"
          content={t("invoiceProcessing.metaDescription")}
        />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Header Component */}
        <Header
          title={t("invoiceProcessing.title")}
          description={t("invoiceProcessing.description")}
        />

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-hero-cta border border-gray-100 dark:border-gray-700 mb-8"
        >
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <Breadcrumb />
              <div>
                <h1 className="text-3xl font-bold text-foreground">
                  {t("invoiceProcessing.title")}
                </h1>
                <p className="text-muted-foreground">
                  {t("invoiceProcessing.description")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="default"
                size="lg"
                onClick={() => setShowBatchModal(true)}
                iconName="Plus"
                iconPosition="left"
              >
                {t("invoiceProcessing.createBatch")}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Queue Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8"
        >
          <QueueOverviewPanel
            totalInvoices={totalInvoices}
            processingCount={processingCount}
            completedCount={completedCount}
            errorCount={errorCount}
            activeBatches={activeBatches}
          />
        </motion.div>

        {/* Main Content */}
        {totalInvoices === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <EmptyQueueState onUploadClick={handleUploadClick} />
          </motion.div>
        ) : (
          <div className="space-y-6">
            {/* View Toggle */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-hero-cta border border-gray-100 dark:border-gray-700"
            >
              <div className="flex items-center justify-between">
                <ViewToggle
                  currentView={currentView}
                  onViewChange={setCurrentView}
                />
                <div className="text-sm text-muted-foreground">
                  {t("invoiceProcessing.lastUpdated", {
                    time: new Date()?.toLocaleTimeString("en-GB"),
                  })}
                </div>
              </div>
            </motion.div>

            {/* Content Views */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 shadow-hero-cta border border-gray-100 dark:border-gray-700"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentView}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {currentView === "timeline" ? (
                    <TimelineView
                      batchInfo={activeBatchInfo}
                      onRetry={handleRetry}
                      onViewDetails={handleViewDetails}
                      onCorrectError={handleCorrectError}
                    />
                  ) : (
                    // <TrackerView
                    //   batchInfo={batchInfo}
                    //   onRetry={handleRetry}
                    //   onViewDetails={handleViewDetails}
                    //   onCorrectError={handleCorrectError}
                    // />
                    <></>
                  )}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </div>
        )}
      </div>

      {/* Batch Creation Modal */}
      <BatchCreationModal
        isOpen={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        onCreateBatch={handleCreateBatch}
      />
    </div>
  );
};

const InvoiceProcessingQueue = () => {
  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />
        <InvoiceProcessingQueueContent />
      </div>
    </SidebarProvider>
  );
};

export default InvoiceProcessingQueue;
